import React, {
  createContext,
  useContext,
  useState,
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useLayoutEffect,
} from "react"
import styles from "./NavigationController.module.scss"

/**
 * Navigation context definitions
 */
interface NavigationContextValue {
  /** Push the next screen in the array or an explicit index. */
  push: (toIndex?: number) => void
  /** Pop the current screen if possible. */
  pop: () => void
  /** Reset back to an optional index (default 0). */
  reset: (toIndex?: number) => void
  /** The index of the screen currently displayed. */
  index: number
  /** `true` when `pop()` is allowed. */
  canPop: boolean
}

const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined
)

export const useNavigation = () => {
  const ctx = useContext(NavigationContext)
  if (!ctx) {
    throw new Error(
      "useNavigation must be used within <NavigationController />"
    )
  }
  return ctx
}

/**
 * Props for NavigationController
 */
export interface NavigationControllerProps {
  /** An ordered array of React elements representing screens. */
  screens: ReactElement[]
  /** Optional starting index (defaults to 0). */
  initialIndex?: number
  /** Optional extra className(s) merged with the default container style. */
  className?: string
  /** Transition duration in milliseconds (defaults to 300). */
  duration?: number
}

/**
 * `NavigationController` — a head‑less navigation stack with iOS‑style push/pop
 * animation implemented with plain CSS + SCSS Modules (no external animation
 * libs or utility classes).
 *
 * ```tsx
 * <NavigationController screens={[<Home />, <Profile />, <Settings />]} />
 * ```
 *
 * Use the `useNavigation()` hook in any descendant to push/pop. All screens are
 * rendered absolutely inside a clipping container so size/control it however
 * you like via `className`.
 */
export const NavigationController: React.FC<NavigationControllerProps> = ({
  screens,
  initialIndex = 0,
  className = "",
  duration = 400,
}) => {
  /* ---------------------------------------------------------------------- */
  /* State                                                                  */
  /* ---------------------------------------------------------------------- */
  const [stack, setStack] = useState<number[]>([initialIndex])
  const [prevIndex, setPrevIndex] = useState<number | null>(null)
  const [direction, setDirection] = useState<number>(0) // 1 push, -1 pop

  const enteringRef = useRef<HTMLDivElement>(null)
  const leavingRef = useRef<HTMLDivElement>(null)

  const currentIndex = stack[stack.length - 1]

  /* ---------------------------------------------------------------------- */
  /* Navigation API                                                         */
  /* ---------------------------------------------------------------------- */
  const push = useCallback(
    (toIndex?: number) => {
      const nextIndex = typeof toIndex === "number" ? toIndex : currentIndex + 1
      if (nextIndex >= screens.length) return // out of bounds – ignore
      setPrevIndex(currentIndex)
      setDirection(1)
      setStack((prev) => [...prev, nextIndex])
    },
    [currentIndex, screens.length]
  )

  const pop = useCallback(() => {
    if (stack.length <= 1) return // can’t pop root
    setPrevIndex(currentIndex)
    setDirection(-1)
    setStack((prev) => prev.slice(0, -1))
  }, [currentIndex, stack.length])

  const reset = useCallback(
    (toIndex = 0) => {
      if (toIndex < 0 || toIndex >= screens.length) return
      setPrevIndex(null)
      setDirection(0)
      setStack([toIndex])
    },
    [screens.length]
  )

  /* ---------------------------------------------------------------------- */
  /* Animation logic (plain CSS)                                            */
  /* ---------------------------------------------------------------------- */
  useLayoutEffect(() => {
    if (prevIndex === null) return // no animation needed

    const entering = enteringRef.current
    const leaving = leavingRef.current
    if (!entering || !leaving) return

    const ms = duration
    const offsetEnter = direction === 1 ? "100%" : "-100%" // off‑screen start
    const offsetLeave = direction === 1 ? "-100%" : "100%" // off‑screen end

    // 1. Set initial positions without transition
    entering.style.transition = "none"
    leaving.style.transition = "none"
    entering.style.transform = `translateX(${offsetEnter})`
    leaving.style.transform = "translateX(0%)"

    // 2. Force reflow so the browser registers initial positions
    void entering.offsetWidth // eslint-disable-line @typescript-eslint/no-unused-expressions

    // 3. Animate to final positions
    entering.style.transition = `transform ${ms}ms ease-out`
    leaving.style.transition = `transform ${ms}ms ease-out`
    entering.style.transform = "translateX(0%)"
    leaving.style.transform = `translateX(${offsetLeave})`

    // 4. Clean up once the animation finishes
    const handleEnd = () => {
      setPrevIndex(null) // drop the leaving screen from the DOM
      leaving.removeEventListener("transitionend", handleEnd)
    }
    leaving.addEventListener("transitionend", handleEnd)

    // Cleanup in case unmounted mid‑animation
    return () => leaving.removeEventListener("transitionend", handleEnd)
  }, [prevIndex, direction, duration])

  /* ---------------------------------------------------------------------- */
  /* Context value                                                          */
  /* ---------------------------------------------------------------------- */
  const contextValue: NavigationContextValue = {
    push,
    pop,
    reset,
    index: currentIndex,
    canPop: stack.length > 1,
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                 */
  /* ---------------------------------------------------------------------- */
  return (
    <NavigationContext.Provider value={contextValue}>
      <div className={`${styles.container} ${className}`.trim()}>
        {prevIndex !== null && (
          <div ref={leavingRef} className={styles.screen}>
            {screens[prevIndex] as ReactNode}
          </div>
        )}
        <div ref={enteringRef} className={styles.screen}>
          {screens[currentIndex] as ReactNode}
        </div>
      </div>
    </NavigationContext.Provider>
  )
}
